import { Suspense, useEffect, useState } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { Draft, Problem } from '../api/types';
import { CodeViewerLazy } from '../code/CodeViewerLazy';
import { FileTree, pickFile } from '../code/FileTreeView';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { Tabs } from '../components/Tabs';
import { usePlatform } from '../app/platform';
import type { DraftDiffView } from '../app/router';
import { toProblem, useAsync } from '../app/useAsync';
import { useStudioEvents } from '../app/useStudioEvents';
import { saveBytes } from '../lib/download';
import { DraftEditor } from './DraftEditor';
import { DraftHistory } from './DraftHistory';

export interface CodeTabProps {
  workspaceId: string;
  appId: string;
  /** URLで指定されているファイル。未指定のときの既定は pickFile が決める。 */
  file: string | null;
  /**
   * app詳細のupdatedAt。再登録（app.updated）で変わるので、これを取得の鍵にすると
   * 稼働中パッケージが入れ替わったときに古いコードを表示したままにしない。
   * Draft も取り直し、取り込み元が現行と違うことを帯で知らせる。
   */
  appUpdatedAt: string | null;
  onSelectFile(path: string): void;
  /** Draft の版の履歴を開いているとき 'history'（URL の codePanel）。 */
  codePanel?: 'history';
  /** 版の履歴で差分を見ている版（URL の rev）。 */
  revision?: string | null;
  /** 差分の視点（URL の diff）。 */
  diffView?: DraftDiffView;
  onSelectCodePanel?(panel: 'history' | null): void;
  onSelectRevision?(revisionId: string | null): void;
  onSelectDiffView?(view: DraftDiffView): void;
}

/**
 * コードタブ。Draft が無ければ稼働中のパッケージを読み取り専用で表示し、
 * Draft があれば Draft のファイルを表示・直接編集する（計画の調整 B2）。
 *
 * Draft の有無は GET A/draft の 404 で決める。fixture には Draft の経路が無いので、
 * どの 404 も「Draft 無し」として読み取り表示に戻す（計画の調整 B16）。
 */
export function CodeTab({
  workspaceId,
  appId,
  file,
  appUpdatedAt,
  onSelectFile,
  codePanel,
  revision,
  diffView,
  onSelectCodePanel,
  onSelectRevision,
  onSelectDiffView,
}: CodeTabProps) {
  const { api, mode } = usePlatform();
  const query = useAsync(
    () => api.getDraft(workspaceId, appId),
    [api, workspaceId, appId, appUpdatedAt],
  );

  // 変更の応答で受け取った Draft は取り直さずにそのまま使い、取り直しが届いたらそちらを正にする。
  // effect で合わせると古い Draft で 1 度描かれるので、描画中に前回の取得結果と比べて入れ替える。
  const [draft, setDraft] = useState<Draft | null>(null);
  const [fetched, setFetched] = useState<Draft | null>(null);
  if (query.data !== fetched) {
    setFetched(query.data);
    // 一度 Draft を出した後、SSE・expired・appUpdatedAt による取り直しが 404 以外で失敗しても
    // Draft 表示ごと外さない（編集中の入力を守る。初回の取得の失敗と 404 の扱いは変えない）。
    const staleReloadFailure =
      draft !== null && query.data === null && query.problem !== null && query.problem.status !== 404;
    if (!staleReloadFailure) setDraft(query.data);
  }
  // 別の app へ移った直後に、前の app で作った Draft を出さない。
  const current = draft !== null && draft.appId === appId ? draft : null;
  // 取り直しが 404 以外で失敗しても current は残したままなので、理由を帯の近くに出す。
  const reloadProblem =
    current !== null && query.data === null && query.problem !== null && query.problem.status !== 404
      ? query.problem
      : null;

  const streamState = useStudioEvents({
    api,
    workspaceId,
    cursor: current?.eventCursor ?? null,
    enabled: mode === 'http' && current !== null,
    onEvent: (event) => {
      // W/studio/events は Workspace 全体を流す（計画の調整 A8）。
      // この app の版が、手元の head と違う版へ進んだときだけ取り直す（自分の保存では取り直さない）。
      if (event.type !== 'studio.revision' || event.appId !== appId) return;
      if (current !== null && event.revisionId === current.headRevisionId) return;
      query.reload();
    },
  });

  useEffect(() => {
    if (streamState === 'expired') query.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamState]);

  if (current) {
    return (
      <div className="stack">
        <DraftBand
          workspaceId={workspaceId}
          appId={appId}
          draft={current}
          panel={codePanel ?? null}
          onSelectPanel={(panel) => onSelectCodePanel?.(panel)}
        />
        <ProblemNotice
          problem={reloadProblem}
          tone={reloadProblem?.status === 503 ? 'notice' : 'error'}
          action={<Button onClick={query.reload}>再取得</Button>}
        />
        {codePanel === 'history' ? (
          <DraftHistory
            workspaceId={workspaceId}
            appId={appId}
            draft={current}
            revisionId={revision ?? null}
            diffView={diffView ?? 'file'}
            onSelectRevision={(revisionId) => onSelectRevision?.(revisionId)}
            onSelectDiffView={(view) => onSelectDiffView?.(view)}
            onDraftChange={setDraft}
          />
        ) : (
          <DraftEditor
            workspaceId={workspaceId}
            appId={appId}
            draft={current}
            file={file}
            onSelectFile={onSelectFile}
            onDraftChange={setDraft}
            onReload={query.reload}
          />
        )}
      </div>
    );
  }
  if (query.loading) return <Spinner label="ファイルを取得しています" />;
  if (query.problem && query.problem.status !== 404) {
    return (
      <ProblemNotice problem={query.problem} action={<Button onClick={query.reload}>再取得</Button>} />
    );
  }
  return (
    <PackageView
      workspaceId={workspaceId}
      appId={appId}
      file={file}
      appUpdatedAt={appUpdatedAt}
      onSelectFile={onSelectFile}
      canCreateDraft={mode === 'http'}
      onDraftCreated={setDraft}
      onDraftExists={query.reload}
    />
  );
}

const CODE_PANELS = [
  { key: 'files', label: 'ファイル' },
  { key: 'history', label: '版の履歴' },
];

interface DraftBandProps {
  workspaceId: string;
  appId: string;
  draft: Draft;
  panel: 'history' | null;
  onSelectPanel(panel: 'history' | null): void;
}

/** Draft の帯。版、取り込み元の注意、ZIP の書き出し、ファイル / 版の履歴の切り替え。 */
function DraftBand({ workspaceId, appId, draft, panel, onSelectPanel }: DraftBandProps) {
  const { api } = usePlatform();
  const [exporting, setExporting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  // 再登録で app の active upload が変わっても、Draft は取り込んだ時点のまま（取り込み直しは残課題）。
  const stale = draft.baseUploadId !== null && draft.baseUploadId !== draft.activeUploadId;

  const exportZip = async () => {
    setExporting(true);
    setProblem(null);
    try {
      // head を ZIP にする。docs/ と models/highlevel/ は入らない（計画の調整 B12）。
      const archive = await api.exportDraft(workspaceId, appId);
      saveBytes(archive.bytes, archive.fileName);
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="draft-band">
      <div className="row">
        <Badge tone="active">Draft</Badge>
        <span className="draft-band-revision">{`版 ${draft.headRevisionId.slice(0, 8)}`}</span>
        <span className="spacer" />
        <Button disabled={exporting} onClick={() => void exportZip()}>
          ZIP を書き出す
        </Button>
      </div>
      {stale && (
        <p className="hint">この Draft は以前のパッケージから取り込みました。現行のパッケージと違う場合があります。</p>
      )}
      <ProblemNotice problem={problem} />
      <Tabs
        label="コードの表示"
        active={panel ?? 'files'}
        onSelect={(key) => onSelectPanel(key === 'history' ? 'history' : null)}
        items={CODE_PANELS}
      />
    </div>
  );
}

interface PackageViewProps {
  workspaceId: string;
  appId: string;
  file: string | null;
  appUpdatedAt: string | null;
  onSelectFile(path: string): void;
  /** 実 API のときだけ Draft を起こせる（fixture には Draft の経路が無い。計画の調整 B16）。 */
  canCreateDraft: boolean;
  onDraftCreated(draft: Draft): void;
  /** 別タブなどで既に Draft があった（409 draft_exists）ときに、Draft 表示へ切り替えさせる。 */
  onDraftExists(): void;
}

/**
 * 稼働中のパッケージに入っていたファイルを読む。編集はしない。
 *
 * 一式は1リクエストで届くので、ファイルを切り替えても取り直さない。表示中の
 * ファイルはURLに載せ、戻る/進むと直接アクセスで同じ場所へ戻れるようにする。
 */
function PackageView({
  workspaceId,
  appId,
  file,
  appUpdatedAt,
  onSelectFile,
  canCreateDraft,
  onDraftCreated,
  onDraftExists,
}: PackageViewProps) {
  const { api } = usePlatform();
  const source = useAsync(
    () => api.getSource(workspaceId, appId),
    [api, workspaceId, appId, appUpdatedAt],
  );
  const [creating, setCreating] = useState(false);
  const [createProblem, setCreateProblem] = useState<Problem | null>(null);
  // 応答だけ失われた再送を重複排除するため、成功するまで同じキーで送る（F3）。
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  const createDraft = async () => {
    setCreating(true);
    setCreateProblem(null);
    try {
      const mutation = await api.createDraft(workspaceId, appId, idempotencyKey);
      // 成功すると呼び出し側が Draft の表示へ切り替え、この表示は外れる。
      onDraftCreated(mutation.draft);
    } catch (error) {
      const failure = toProblem(error);
      // 別タブなどで既に作られていた場合は失敗として出さず、Draft の表示へ切り替える。
      if (failure.code === 'draft_exists') {
        onDraftExists();
        return;
      }
      setCreateProblem(failure);
    } finally {
      setCreating(false);
    }
  };

  if (source.loading) return <Spinner label="ファイルを取得しています" />;
  if (source.problem) {
    return (
      <ProblemNotice problem={source.problem} action={<Button onClick={source.reload}>再取得</Button>} />
    );
  }
  if (!source.data) return null;
  if (source.data.paths.length === 0) {
    return <EmptyState title="表示できるファイルがありません" />;
  }

  const current = pickFile(source.data.paths, file);
  const content = source.data.contents.get(current) ?? '';

  return (
    <div className="stack">
      {canCreateDraft && (
        <div className="draft-band">
          <div className="row">
            <Button variant="primary" disabled={creating} onClick={() => void createDraft()}>
              Draft を作って編集する
            </Button>
            <span className="hint">
              現行のパッケージを取り込んだ作業コピーを作ります。稼働中の microapp は変わりません。
            </span>
          </div>
          <ProblemNotice problem={createProblem} tone={createProblem?.status === 503 ? 'notice' : 'error'} />
        </div>
      )}
      <div className="code-layout">
        <FileTree paths={source.data.paths} current={current} onSelectFile={onSelectFile} />

        <section className="code-editor">
          <div className="code-bar">
            <code>{current}</code>
          </div>
          <Suspense fallback={<Spinner label="表示を準備しています" />}>
            <CodeViewerLazy path={current} content={content} />
          </Suspense>
          <div className="code-footer">
            登録されたZIPの中身を表示しています。ここでは編集できません。
            {source.data.omitted.length > 0 &&
              `　·　画像など表示できないファイル ${source.data.omitted.length} 件`}
          </div>
        </section>
      </div>
    </div>
  );
}
